"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SendTab = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * Copyright 2025 Scott Bender (scott@scottbender.net)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const react_1 = require("react");
const reactstrap_1 = require("reactstrap");
const canboatjs_1 = require("@canboat/canboatjs");
const localStorage_1 = require("../utils/localStorage");
const services_1 = require("../services");
const SendTab = () => {
    const [sendStatus, setSendStatus] = (0, react_1.useState)({
        sending: false,
    });
    const [isJsonInput, setIsJsonInput] = (0, react_1.useState)(false);
    const [convertedJson, setConvertedJson] = (0, react_1.useState)('');
    const [messageHistory, setMessageHistory] = (0, react_1.useState)([]);
    const [showHistoryDropdown, setShowHistoryDropdown] = (0, react_1.useState)(false);
    // Load message history from localStorage on component mount
    (0, react_1.useEffect)(() => {
        const savedHistory = localStorage_1.sendTabStorage.getMessageHistory();
        // Convert MessageHistoryItem[] to MessageHistory[] (ensure format is string)
        const convertedHistory = savedHistory.map((item) => ({
            message: item.message,
            timestamp: item.timestamp,
            format: item.format || 'unknown',
        }));
        setMessageHistory(convertedHistory);
    }, []);
    // Save message history to localStorage whenever it changes
    (0, react_1.useEffect)(() => {
        localStorage_1.sendTabStorage.setMessageHistory(messageHistory);
    }, [messageHistory]);
    // Function to detect message format
    const detectMessageFormat = (message) => {
        const trimmed = message.trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            return 'JSON';
        }
        else {
            return 'String';
        }
    };
    // Function to get a readable description for a message
    const getMessageDescription = (message, format) => {
        // For multi-line messages, only show the first line
        const firstLine = message.split('\n')[0];
        if (format !== 'JSON') {
            return firstLine;
        }
        try {
            const parsed = JSON.parse(message);
            if (Array.isArray(parsed)) {
                // For arrays, show count and first PGN info
                if (parsed.length > 0 && parsed[0].pgn) {
                    const firstPgn = parsed[0];
                    const desc = firstPgn.description || `PGN ${firstPgn.pgn}`;
                    return `${parsed.length} messages: ${desc}${parsed.length > 1 ? ', ...' : ''}`;
                }
                return `${parsed.length} messages`;
            }
            else {
                // For single objects, show PGN description
                if (parsed.pgn) {
                    return parsed.description || `PGN ${parsed.pgn}`;
                }
                return 'JSON message';
            }
        }
        catch (error) {
            return firstLine;
        }
    };
    // Function to add message to history
    const addToHistory = (message) => {
        const historyItem = {
            message,
            timestamp: new Date().toISOString(),
            format: detectMessageFormat(message),
        };
        setMessageHistory((prev) => {
            // Remove duplicate if exists
            const filtered = prev.filter((item) => item.message !== message);
            // Add new item at the beginning and limit to 20 items
            return [historyItem, ...filtered].slice(0, 20);
        });
    };
    // Function to select message from history
    const selectFromHistory = (message) => {
        const textarea = document.getElementById('nmea2000Message');
        if (textarea) {
            textarea.value = message;
            checkJsonInput(message);
        }
        setShowHistoryDropdown(false);
    };
    // Function to clear history
    const clearHistory = () => {
        if (messageHistory.length > 0 && !confirm('Are you sure you want to clear all message history?')) {
            return;
        }
        setMessageHistory([]);
        setShowHistoryDropdown(false);
    };
    // Function to delete a specific history item
    const deleteHistoryItem = (index, event) => {
        event.stopPropagation(); // Prevent selecting the item when deleting
        setMessageHistory((prev) => prev.filter((_, i) => i !== index));
    };
    // Function to check if input is JSON and update state
    const checkJsonInput = (input) => {
        const trimmed = input.trim();
        const isJson = (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));
        setIsJsonInput(isJson && trimmed.length > 0);
        // If not JSON and has content, show converted JSON
        if (!isJson && trimmed.length > 0) {
            convertToCanboatJson(trimmed);
        }
        else {
            setConvertedJson('');
        }
    };
    // Function to convert non-JSON input to canboat JSON format
    const convertToCanboatJson = (input) => {
        const lines = input.split('\n').filter((line) => line.trim());
        const jsonMessages = [];
        // Create parser instance
        const parser = new canboatjs_1.FromPgn({
            returnNulls: true,
            checkForInvalidFields: true,
            useCamel: true,
            useCamelCompat: false,
            returnNonMatches: true,
            createPGNObjects: true,
            includeInputData: true,
        });
        parser.on('error', (pgn, error) => {
            console.error(`Error parsing ${pgn.pgn} ${error}`);
            //console.error(error.stack)
        });
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed)
                continue;
            try {
                // Try to parse using canboatjs parseString method
                const parsed = parser.parseString(trimmed);
                if (parsed) {
                    jsonMessages.push(parsed);
                }
            }
            catch (error) {
                // If parsing fails, skip this line
                console.warn('Failed to parse line:', trimmed, error);
                continue;
            }
        }
        if (jsonMessages.length > 0) {
            const formatted = jsonMessages.length === 1 ? JSON.stringify(jsonMessages[0], null, 2) : JSON.stringify(jsonMessages, null, 2);
            setConvertedJson(formatted);
        }
        else {
            setConvertedJson('');
        }
    };
    // Function to beautify JSON
    const handleBeautifyJson = () => {
        const textarea = document.getElementById('nmea2000Message');
        if (!textarea)
            return;
        try {
            const input = textarea.value.trim();
            if ((input.startsWith('{') && input.endsWith('}')) || (input.startsWith('[') && input.endsWith(']'))) {
                const parsed = JSON.parse(input);
                const beautified = JSON.stringify(parsed, null, 2);
                textarea.value = beautified;
                // Update JSON detection after beautifying
                checkJsonInput(beautified);
            }
        }
        catch (error) {
            // If JSON parsing fails, don't modify the input
            console.warn('Could not beautify JSON:', error);
        }
    };
    // Function to handle sending NMEA 2000 messages
    const handleSendMessage = async () => {
        const textarea = document.getElementById('nmea2000Message');
        if (!textarea || !textarea.value.trim()) {
            setSendStatus({
                sending: false,
                error: 'Please enter a message to send',
            });
            return;
        }
        setSendStatus({ sending: true, error: undefined });
        try {
            const input = textarea.value.trim();
            // Use the centralized service to send the message
            await services_1.server.post({ type: 'send-n2k', values: [input] });
            setSendStatus({
                sending: false,
                lastSent: new Date().toLocaleTimeString(),
                error: undefined,
            });
            // Add successful message to history
            addToHistory(input);
            console.log('Successfully sent message(s)');
        }
        catch (error) {
            console.error('Error sending message:', error);
            setSendStatus({
                sending: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred while sending message',
            });
        }
    };
    return ((0, jsx_runtime_1.jsx)("div", { className: "row mb-4", children: (0, jsx_runtime_1.jsx)("div", { className: "col-12", children: (0, jsx_runtime_1.jsxs)(reactstrap_1.Card, { children: [(0, jsx_runtime_1.jsx)(reactstrap_1.CardHeader, { children: (0, jsx_runtime_1.jsx)("h6", { className: "mb-0", children: "Send Raw NMEA 2000 Message" }) }), (0, jsx_runtime_1.jsxs)(reactstrap_1.CardBody, { children: [(0, jsx_runtime_1.jsx)("p", { className: "card-text small mb-3", children: "Enter NMEA 2000 messages in any format supported by canboatjs (Actisense, YDRAW, Canboat JSON, etc.). Multiple messages can be entered on separate lines." }), (0, jsx_runtime_1.jsxs)("div", { className: "row mb-3", children: [(0, jsx_runtime_1.jsx)("div", { className: convertedJson ? 'col-md-6' : 'col-12', children: (0, jsx_runtime_1.jsxs)("div", { className: "form-group", children: [(0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [(0, jsx_runtime_1.jsx)("label", { htmlFor: "nmea2000Message", className: "form-label mb-0", children: (0, jsx_runtime_1.jsx)("strong", { children: "NMEA 2000 Message:" }) }), messageHistory.length > 0 && ((0, jsx_runtime_1.jsxs)("div", { className: "position-relative", children: [(0, jsx_runtime_1.jsxs)("button", { type: "button", className: "btn btn-outline-secondary btn-sm", onClick: () => setShowHistoryDropdown(!showHistoryDropdown), children: [(0, jsx_runtime_1.jsx)("i", { className: "fas fa-history me-1" }), "History (", messageHistory.length, ")"] }), showHistoryDropdown && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { className: "position-fixed top-0 start-0 w-100 h-100", style: { zIndex: 1040 }, onClick: () => setShowHistoryDropdown(false) }), (0, jsx_runtime_1.jsxs)("div", { className: "position-absolute bg-white border rounded shadow-lg", style: {
                                                                                top: '100%',
                                                                                right: 0,
                                                                                minWidth: '400px',
                                                                                maxHeight: '300px',
                                                                                overflowY: 'auto',
                                                                                zIndex: 1050,
                                                                                marginTop: '4px',
                                                                            }, children: [(0, jsx_runtime_1.jsx)("div", { className: "p-2 border-bottom bg-light", children: (0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-center", children: [(0, jsx_runtime_1.jsx)("small", { className: "text-muted fw-bold", children: "Message History" }), (0, jsx_runtime_1.jsx)("div", { className: "d-flex gap-2", children: (0, jsx_runtime_1.jsxs)("button", { type: "button", className: "btn btn-outline-danger btn-sm", onClick: clearHistory, title: "Clear all history", style: { fontSize: '0.75em', padding: '2px 8px' }, children: [(0, jsx_runtime_1.jsx)("i", { className: "fas fa-trash me-1" }), "Clear All"] }) })] }) }), messageHistory.map((item, index) => ((0, jsx_runtime_1.jsx)("div", { className: "p-2 border-bottom cursor-pointer", style: {
                                                                                        cursor: 'pointer',
                                                                                        transition: 'background-color 0.2s',
                                                                                    }, onClick: () => selectFromHistory(item.message), onMouseEnter: (e) => {
                                                                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                                                    }, onMouseLeave: (e) => {
                                                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                                                    }, children: (0, jsx_runtime_1.jsxs)("div", { className: "d-flex justify-content-between align-items-start", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex-grow-1", style: { minWidth: 0 }, children: [(0, jsx_runtime_1.jsxs)("div", { className: "d-flex align-items-center mb-1", children: [(0, jsx_runtime_1.jsx)("span", { className: "badge bg-secondary me-2", style: { fontSize: '0.7em' }, children: item.format }), (0, jsx_runtime_1.jsx)("small", { className: "text-muted", children: new Date(item.timestamp).toLocaleString() })] }), (0, jsx_runtime_1.jsx)("div", { className: "font-monospace small text-truncate", style: { fontSize: '0.75em' }, title: item.message, children: getMessageDescription(item.message, item.format) })] }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "btn btn-sm text-danger p-1", onClick: (e) => deleteHistoryItem(index, e), title: "Delete this item", style: {
                                                                                                    fontSize: '14px',
                                                                                                    lineHeight: 1,
                                                                                                    minWidth: '22px',
                                                                                                    height: '22px',
                                                                                                    display: 'flex',
                                                                                                    alignItems: 'center',
                                                                                                    justifyContent: 'center',
                                                                                                    border: '1px solid #dc3545',
                                                                                                    borderRadius: '3px',
                                                                                                    backgroundColor: 'white',
                                                                                                    fontWeight: 'bold',
                                                                                                }, children: "\u00D7" })] }) }, index)))] })] }))] }))] }), (0, jsx_runtime_1.jsx)("textarea", { id: "nmea2000Message", className: "form-control font-monospace", rows: 12, placeholder: 'Enter NMEA 2000 message here...\n\nActisense format:\n2023-10-15T10:30:00.000Z,2,127251,1,255,8,ff,ff,ff,ff,ff,ff,ff,ff\n\nYDRAW format:\n21:53:15.000 R 0DF80503 FF FF FF FF FF FF FF FF\n\nCanboat JSON format:\n{"timestamp":"2023-10-15T10:30:00.000Z","prio":2,"src":1,"dst":255,"pgn":127251,"description":"Rate of Turn","fields":{"Rate":0}}\n\nJSON array (multiple messages):\n[{"pgn":127251,"src":1,"fields":{"Rate":0}},{"pgn":127250,"src":1,"fields":{"Heading":1.5708}}]\n\nMultiple messages (any format, one per line):\n2023-10-15T10:30:00.000Z,2,127251,1,255,8,ff,ff,ff,ff,ff,ff,ff,ff\n21:53:16.000 R 0DF80503 01 02 03 04 05 06 07 08', onChange: (e) => checkJsonInput(e.target.value), spellCheck: false, style: {
                                                        fontSize: '14px',
                                                        lineHeight: '1.4',
                                                    } })] }) }), convertedJson && ((0, jsx_runtime_1.jsx)("div", { className: "col-md-6", children: (0, jsx_runtime_1.jsxs)("div", { className: "form-group", children: [(0, jsx_runtime_1.jsx)("label", { className: "form-label", children: (0, jsx_runtime_1.jsx)("strong", { children: "Canboat JSON:" }) }), (0, jsx_runtime_1.jsx)("textarea", { className: "form-control font-monospace", rows: 12, value: convertedJson, readOnly: true, style: {
                                                        fontSize: '14px',
                                                        lineHeight: '1.4',
                                                        backgroundColor: '#f8f9fa',
                                                        color: '#495057',
                                                    } })] }) }))] }), (0, jsx_runtime_1.jsxs)("div", { className: "d-flex mb-3", children: [(0, jsx_runtime_1.jsx)("button", { type: "button", className: "btn btn-primary me-3", disabled: sendStatus.sending, onClick: handleSendMessage, children: sendStatus.sending ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("div", { className: "spinner-border spinner-border-sm me-2", role: "status" }), "Sending..."] })) : ('Send Message') }), isJsonInput && ((0, jsx_runtime_1.jsx)("button", { type: "button", className: "btn btn-info me-3", disabled: sendStatus.sending, onClick: handleBeautifyJson, children: "Beautify JSON" })), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "btn btn-secondary", disabled: sendStatus.sending, onClick: () => {
                                            const textarea = document.getElementById('nmea2000Message');
                                            if (textarea) {
                                                textarea.value = '';
                                                checkJsonInput('');
                                            }
                                            setSendStatus({ sending: false });
                                            setIsJsonInput(false);
                                            setConvertedJson('');
                                        }, children: "Clear" })] }), sendStatus.error && ((0, jsx_runtime_1.jsxs)("div", { className: "alert alert-danger", role: "alert", children: [(0, jsx_runtime_1.jsx)("i", { className: "fas fa-exclamation-triangle me-2" }), (0, jsx_runtime_1.jsx)("strong", { children: "Send Error:" }), " ", sendStatus.error] })), sendStatus.lastSent && !sendStatus.error && ((0, jsx_runtime_1.jsxs)("div", { className: "alert alert-success", role: "alert", children: [(0, jsx_runtime_1.jsx)("i", { className: "fas fa-check-circle me-2" }), (0, jsx_runtime_1.jsx)("strong", { children: "Success:" }), " Message(s) sent successfully at ", sendStatus.lastSent] }))] })] }) }) }));
};
exports.SendTab = SendTab;
